import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import {
  Grid,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  TableContainer,
  Typography,
  Chip,
  InputAdornment,
  TextField,
  alpha,
  Tooltip,
  Select,
  MenuItem,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import * as actions from "./actions";
import moment from "moment";
import { Link, useHistory, useLocation } from "react-router-dom";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import AlbumIcon from '@mui/icons-material/Album';
import ImageIcon from '@mui/icons-material/Image';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import Pagination from '@mui/material/Pagination';
import _ from "lodash";
import { API_URL } from '../../constants';

// Styled Components
const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

const PageTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '1.5rem',
  color: '#F1F5F9',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

const StatsChip = styled(Chip)({
  background: 'linear-gradient(135deg, rgba(18, 180, 161, 0.15) 0%, rgba(5, 150, 179, 0.15) 100%)',
  border: '1px solid rgba(18, 180, 161, 0.3)',
  color: '#12B4A1',
  fontWeight: 600,
  fontSize: '0.875rem',
  height: 32,
  '& .MuiChip-label': {
    padding: '0 12px',
  },
});

const SearchField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    borderRadius: 12,
    color: '#F1F5F9',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.1)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(18, 180, 161, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#12B4A1',
      borderWidth: 2,
    },
  },
  '& .MuiInputAdornment-root': {
    color: '#64748B',
  },
  '& input::placeholder': {
    color: '#64748B',
    opacity: 1,
  },
});

const GlassCard = styled(Paper)({
  borderRadius: 16,
  background: 'rgba(30, 41, 59, 0.5)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  overflow: 'hidden',
});

const StyledTableContainer = styled(TableContainer)({
  background: 'transparent',
  boxShadow: 'none',
  border: 'none',
  '&.MuiPaper-root': {
    backgroundColor: 'transparent',
    backgroundImage: 'none',
  },
});

const StyledTableHead = styled(TableHead)({
  '& .MuiTableCell-root': {
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    color: '#94A3B8',
    fontWeight: 600,
    fontSize: '0.75rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    padding: '14px 16px',
    whiteSpace: 'nowrap',
  },
});

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(18, 180, 161, 0.05)',
  },
  '& .MuiTableCell-root': {
    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
    color: '#E2E8F0',
    padding: '12px 16px',
  },
}));

const TitleCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

const AlbumArt = styled(Box)({
  width: 50,
  height: 50,
  borderRadius: 8,
  backgroundColor: 'rgba(18, 180, 161, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  flexShrink: 0,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  '& svg': {
    fontSize: 22,
    color: alpha('#12B4A1', 0.5),
  },
});

const TitleLink = styled(Link)({
  color: '#F1F5F9',
  textDecoration: 'none',
  fontWeight: 500,
  transition: 'color 0.2s ease',
  '&:hover': {
    color: '#12B4A1',
  },
});

const StatusChip = styled(Chip)(({ status }) => {
  const statusStyles = {
    in_review: {
      bg: 'rgba(255, 167, 38, 0.12)',
      color: '#FFA726',
      border: 'rgba(255, 167, 38, 0.3)',
    },
    approved: {
      bg: 'rgba(102, 187, 106, 0.12)',
      color: '#66BB6A',
      border: 'rgba(102, 187, 106, 0.3)',
    },
    rejected: {
      bg: 'rgba(255, 107, 107, 0.12)',
      color: '#FF6B6B',
      border: 'rgba(255, 107, 107, 0.3)',
    },
    takendown: {
      bg: 'rgba(255, 107, 107, 0.12)',
      color: '#FF6B6B',
      border: 'rgba(255, 107, 107, 0.3)',
    },
  };
  const style = statusStyles[status] || statusStyles.in_review;
  return {
    backgroundColor: style.bg,
    color: style.color,
    border: `1px solid ${style.border}`,
    fontWeight: 600,
    fontSize: '0.7rem',
    height: 26,
    '& .MuiChip-icon': {
      color: style.color,
      fontSize: 14,
    },
  };
});

const EmptyState = styled(Box)({
  textAlign: 'center',
  padding: '48px 24px',
  color: '#64748B',
});

const StatusFilterSelect = styled(Select)({
  backgroundColor: 'rgba(15, 23, 42, 0.5)',
  borderRadius: 12,
  color: '#F1F5F9',
  minWidth: 220,
  height: 40,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(18, 180, 161, 0.5)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#12B4A1',
    borderWidth: 2,
  },
  '& .MuiSelect-icon': {
    color: '#64748B',
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 14px',
  },
});

const StatusMenuItem = styled(MenuItem)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  '&.Mui-selected': {
    backgroundColor: 'rgba(18, 180, 161, 0.1)',
  },
  '&:hover': {
    backgroundColor: 'rgba(18, 180, 161, 0.05)',
  },
});

const statusOptions = [
  {
    value: 'all',
    label: 'All Statuses',
    icon: <FilterListIcon sx={{ fontSize: 20, color: '#12B4A1' }} />
  },
  {
    value: 'in_review',
    label: 'In Review',
    icon: <AccessTimeIcon sx={{ fontSize: 20, color: '#FFA726' }} />
  },
  {
    value: 'approved',
    label: 'Approved',
    icon: <CheckCircleIcon sx={{ fontSize: 20, color: '#66BB6A' }} />
  },
  {
    value: 'rejected',
    label: 'Rejected',
    icon: <CancelIcon sx={{ fontSize: 20, color: '#FF6B6B' }} />
  },
  {
    value: 'takendown',
    label: 'Takendown',
    icon: <RemoveCircleOutlineIcon sx={{ fontSize: 20, color: '#FF6B6B' }} />
  },
];

export default function AudioCatalog(props) {
  const history = useHistory();
  const location = useLocation();

  const [selectedStatus, setSelectedStatus] = useState('all');
  const [releases, setReleases] = useState([]);
  const [filters, setFilters] = useState({
    sort: 'all',
    limit: 50,
    offset: 0,
    page: 1,
  });
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState('');
  const [brokenImages, setBrokenImages] = useState({});

  const handleImageError = (releaseId) => {
    setBrokenImages(prev => ({ ...prev, [releaseId]: true }));
  };

  // Read URL parameter on mount and when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get('status') || 'all';
    const validStatuses = ['in_review', 'approved', 'rejected', 'takendown', 'all'];

    if (validStatuses.includes(statusParam)) {
      setSelectedStatus(statusParam);
      setFilters(prev => ({ ...prev, sort: statusParam, page: 1, offset: 0 }));
    } else {
      // Invalid parameter, redirect to default
      history.replace('/audio-catalog?status=all');
    }
  }, [location.search]);

  const onGetNewReleases = useCallback((filters) => {
    props.setLoader(true);
    actions.getNewReleasesListAdmin(filters).then((res) => {
      props.setLoader(false);
      setReleases(res.body);
      setCount(res.total);
    }).catch(() => {
      props.setLoader(false);
    });
  }, []);

  const getSearchReleases = useCallback((filters, searchType) => {
    props.setLoader(true);
    actions.searchInReleases({ ...filters, type: searchType }).then((res) => {
      props.setLoader(false);
      setReleases(res.body);
    }).catch(() => {
      props.setLoader(false);
    });
  }, []);

  const delaySearch = useMemo(
    () => _.debounce((filters, searchType) => getSearchReleases(filters, searchType), 500),
    [getSearchReleases]
  );

  useEffect(() => {
    if (!search) {
      onGetNewReleases(filters);
    }
  }, [filters.sort, filters.limit, filters.offset, filters.page]);

  const handleStatusChange = (newStatus) => {
    setSelectedStatus(newStatus);
    history.push(`/audio-catalog?status=${newStatus}`);
    setFilters({ ...filters, sort: newStatus, page: 1, offset: 0 });
    setSearch(''); // Clear search when changing status
  };

  const onPageChange = (event, page) => {
    const offset = (page - 1) * filters.limit;
    setFilters({ ...filters, page, offset });
  };

  const searchReleases = (value) => {
    setSearch(value);
    if (!value) {
      onGetNewReleases(filters);
    } else {
      // Always search across all statuses regardless of filter
      delaySearch({ search: value }, 'all');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircleIcon />;
      case 'rejected':
        return <CancelIcon />;
      case 'takendown':
        return <RemoveCircleOutlineIcon />;
      default:
        return <AccessTimeIcon />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'takendown':
        return 'Takendown';
      default:
        return 'In Review';
    }
  };

  let totalPages = Math.ceil(Number(count) / 50);
  if (search) totalPages = 1;

  return (
    <Box>
      <Helmet>
        <title>Audio Catalog - Fronic Media Labels</title>
      </Helmet>
      <PageHeader>
        <PageTitle>
          <AlbumIcon sx={{ color: '#12B4A1', fontSize: 28 }} />
          Audio Catalog
        </PageTitle>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <StatsChip label={`Total: ${count}`} />
          <StatusFilterSelect
            value={selectedStatus}
            onChange={(e) => handleStatusChange(e.target.value)}
            size="small"
            aria-label="Filter by release status"
          >
            {statusOptions.map((option) => (
              <StatusMenuItem key={option.value} value={option.value}>
                {option.icon}
                <Typography sx={{ fontSize: '0.875rem' }}>{option.label}</Typography>
              </StatusMenuItem>
            ))}
          </StatusFilterSelect>
        </Box>
      </PageHeader>

      <Box sx={{ mb: 3 }}>
        <SearchField
          size="small"
          placeholder="Search by title, artist, or label (searches across all statuses)..."
          value={search}
          onChange={(e) => searchReleases(e.target.value)}
          sx={{ width: '50%' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <GlassCard elevation={0}>
        <StyledTableContainer component={Paper} elevation={0}>
          <Table>
            <StyledTableHead>
              <TableRow>
                <TableCell sx={{ width: 60 }}>#</TableCell>
                <TableCell>Release</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Label</TableCell>
                <TableCell align="center">Tracks</TableCell>
                <TableCell>Release Date</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {releases.length > 0 ? (
                releases.map((item, key) => (
                  <StyledTableRow key={key}>
                    <TableCell>
                      <Typography sx={{ color: '#64748B', fontSize: '0.875rem' }}>
                        {(filters.page - 1) * filters.limit + (key + 1)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TitleCell>
                        <Tooltip
                          title={
                            item.album_artwork && !brokenImages[item.release_id] ? (
                              <Box sx={{ p: 0.5 }}>
                                <img
                                  src={`${API_URL}/${item.album_artwork}`}
                                  alt="Preview"
                                  style={{
                                    width: 150,
                                    height: 150,
                                    objectFit: 'cover',
                                    borderRadius: 8,
                                    display: 'block',
                                  }}
                                />
                              </Box>
                            ) : 'Image not available'
                          }
                          placement="right"
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: 'rgba(30, 41, 59, 0.95)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: 2,
                                p: 0.5,
                              },
                            },
                            arrow: {
                              sx: {
                                color: 'rgba(30, 41, 59, 0.95)',
                              },
                            },
                          }}
                        >
                          <AlbumArt sx={{ cursor: 'pointer' }}>
                            {item.album_artwork && !brokenImages[item.release_id] ? (
                              <img
                                src={`${API_URL}/${item.album_artwork}`}
                                alt="Artwork"
                                onError={() => handleImageError(item.release_id)}
                              />
                            ) : (
                              <ImageIcon />
                            )}
                          </AlbumArt>
                        </Tooltip>
                        <Tooltip title={item.release_title || '-'} placement="top">
                          <TitleLink to={`/view_releases/${item.release_id}`}>
                            {item.release_title?.length > 30
                              ? `${item.release_title.substring(0, 30)}...`
                              : item.release_title || '-'}
                          </TitleLink>
                        </Tooltip>
                      </TitleCell>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500, color: '#E2E8F0' }}>
                        {item.name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: '#94A3B8', fontSize: '0.875rem' }}>
                        {item.email || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: '#94A3B8', fontSize: '0.875rem' }}>
                        {item.lable_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={item.number_of_tracks || '0'}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(18, 180, 161, 0.1)',
                          color: '#12B4A1',
                          fontWeight: 600,
                          minWidth: 32,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: '#94A3B8', fontSize: '0.875rem' }}>
                        {moment(Number(item.release_date)).format('MMM DD, YYYY')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <StatusChip
                        icon={getStatusIcon(item.status)}
                        label={getStatusLabel(item.status)}
                        status={item.status}
                        size="small"
                      />
                    </TableCell>
                  </StyledTableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8}>
                    <EmptyState>
                      <AlbumIcon sx={{ fontSize: 48, color: '#334155', mb: 2 }} />
                      <Typography variant="body1" sx={{ color: '#64748B' }}>
                        No releases found
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#475569', mt: 1 }}>
                        Try adjusting your search criteria or filter
                      </Typography>
                    </EmptyState>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
        {totalPages > 1 && (
          <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}>
            <Pagination
              count={totalPages}
              showFirstButton
              showLastButton
              onChange={onPageChange}
              page={filters.page || 1}
            />
          </Box>
        )}
      </GlassCard>
    </Box>
  );
}
